import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, BookOpen, Star, Pencil, Trash2, X, Save, Upload } from 'lucide-react';
import { BulkImportModal } from '../ui/BulkImportModal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';

function StarRating({ value, onChange, readonly = false }) {
  return (
    <div className="star-rating flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <button key={s} type="button" className={cn("star", s <= value ? "filled" : "empty")} onClick={() => !readonly && onChange?.(s)} disabled={readonly}>
          <Star className="w-3.5 h-3.5" fill={s <= value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

const genId = (prefix) => `${prefix}_${Math.random().toString(36).substring(2, 9)}`;

export function CoursesList({ courses, branches, onUpdateCourses, selectedCourseId, focusedId, onSelect, readOnly, onRefresh }) {
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [importEntity, setImportEntity] = useState('courses');

  const openNew = () => {
    onSelect({ id: 'new', name: '', code: '', priority: 3, department: '', description: '' }, 'course');
  };

  return (
    <>
    <BulkImportModal
      open={bulkImportOpen}
      onOpenChange={setBulkImportOpen}
      entityName={importEntity}
      onImportComplete={() => { onRefresh?.(); }}
    />
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700/50">
        <div>
          <h2 className="text-base font-bold text-zinc-800 dark:text-zinc-100 tracking-tight">Courses</h2>
          <p className="text-[0.65rem] text-zinc-500 mt-0.5">{courses.length} courses</p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 px-2 flex items-center gap-1"
              onClick={() => { setImportEntity('courses'); setBulkImportOpen(true); }}
              title="Bulk Import Courses"
            >
              <Upload className="w-3.5 h-3.5" />
              <span className="text-[10px] hidden md:inline">Courses</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 px-2 flex items-center gap-1"
              onClick={() => { setImportEntity('subjects'); setBulkImportOpen(true); }}
              title="Bulk Import Subjects"
            >
              <Upload className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-[10px] hidden md:inline">Subjects</span>
            </Button>
            <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={openNew}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
        )}
      </div>
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-2">
          {courses.map((course) => {
            const subjects = course.subjects || [];
            const totalCredits = subjects.reduce((s, sub) => s + (sub.credit_hours || 0), 0);
            return (
              <div key={course.id} className={cn(
                "data-card !p-3 cursor-pointer transition-all",
                selectedCourseId === course.id && "ring-2 ring-blue-500/30",
                focusedId === course.id && "ring-2 ring-violet-500/50 shadow-md"
              )} onClick={() => onSelect(course, 'course')} data-card-index={`course-${course.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center shrink-0">
                      <BookOpen className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">{course.name}</h4>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500">
                        <Badge variant="secondary" className="text-[0.55rem] h-4">{course.code}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StarRating value={course.priority} readonly />
                    <span className="text-[0.6rem] text-zinc-400">{totalCredits} hrs</span>
                  </div>
                </div>
              </div>
            );
          })}
          {courses.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">No courses yet.</p>}
        </div>
      </ScrollArea>
    </div>
    </>
  );
}

export function CourseDetail({ course, branches, onUpdateCourses, onClose, readOnly }) {
  const [editMode, setEditMode] = useState(course?.id === 'new');
  const [editForm, setEditForm] = useState({});
  const [subjectForm, setSubjectForm] = useState({ name: '', credit_hours: 3, completed_hours: 0 });
  const [editingSubject, setEditingSubject] = useState(null);
  const [newCourseSubjects, setNewCourseSubjects] = useState([]);
  const [newSubForm, setNewSubForm] = useState({ name: '', credit_hours: 3 });

  React.useEffect(() => {
    if (course?.id === 'new') {
      setEditMode(true);
      setEditForm({ name: '', code: '', priority: 3, department: '', description: '' });
      setNewCourseSubjects([]);
      setNewSubForm({ name: '', credit_hours: 3 });
    } else {
      setEditMode(false);
      setEditForm({});
    }
  }, [course?.id]);

  const startEdit = () => {
    setEditMode(true);
    setEditForm({ 
      name: course.name, 
      code: course.code, 
      priority: course.priority,
      department: course.department || '',
      description: course.description || ''
    });
  };

  const saveEdit = async () => {
    if (!editForm.name || !editForm.code) { toast.error('Name and code are required'); return; }
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      if (course.id === 'new') {
        const { data, error } = await supabase
          .from('courses')
          .insert({ 
              name: editForm.name, 
              code: editForm.code, 
              priority: editForm.priority || 3, 
              department: editForm.department || null,
              description: editForm.description || null,
              team_id 
          })
          .select()
          .single();
        if (error) {
            if (error.code === '23505') throw new Error('Course code already exists');
            throw error;
        }
        
        let insertedSubjects = [];
        if (newCourseSubjects.length > 0) {
          const subjectsToInsert = newCourseSubjects.map(sub => ({
            name: sub.name,
            credit_hours: sub.credit_hours,
            completed_hours: 0,
            course_id: data.id,
            team_id
          }));
          const { data: subsData, error: subsErr } = await supabase
            .from('subjects')
            .insert(subjectsToInsert)
            .select();
          if (subsErr) throw subsErr;
          insertedSubjects = subsData || [];
        }

        onUpdateCourses(prev => [...prev, { ...data, subjects: insertedSubjects }]);
        toast.success('Course created');
        onClose();
      } else {
        const { data, error } = await supabase
          .from('courses')
          .update(editForm)
          .eq('id', course.id)
          .eq('team_id', team_id)
          .select()
          .single();
        if (error) throw error;
        onUpdateCourses(prev => prev.map(c => c.id === course.id ? { ...c, ...data, subjects: c.subjects } : c));
        toast.success('Course updated');
        setEditMode(false);
      }
    } catch (err) { toast.error(err.message || 'Failed to save course'); }
  };

  const removeCourse = async () => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { error } = await supabase.from('courses').delete().eq('id', course.id).eq('team_id', team_id);
      if (error) throw error;
      onUpdateCourses(prev => prev.filter(c => c.id !== course.id));
      onClose();
      toast.success('Course deleted');
    } catch (err) { toast.error(err.message || 'Failed to delete course'); }
  };

  const addSubject = async () => {
    if (!subjectForm.name) return;
    const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
    try {
      const { data, error } = await supabase
        .from('subjects')
        .insert({ ...subjectForm, course_id: course.id, team_id: profile.team_id })
        .select()
        .single();
      if (error) throw error;
      onUpdateCourses(prev => prev.map(c => c.id === course.id ? { ...c, subjects: [...(c.subjects || []), data] } : c));
      setSubjectForm({ name: '', credit_hours: 3 });
      toast.success('Subject added');
    } catch (err) { toast.error(err.message || 'Failed to add subject'); }
  };

  const updateSubject = async (formData) => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { data, error } = await supabase
        .from('subjects')
        .update(formData)
        .eq('id', editingSubject.id)
        .eq('team_id', team_id)
        .select()
        .single();
      if (error) throw error;
      onUpdateCourses(prev => prev.map(c => c.id === course.id ? { ...c, subjects: (c.subjects || []).map(s => s.id === data.id ? data : s) } : c));
      setEditingSubject(null);
      toast.success('Subject updated');
    } catch (err) { toast.error(err.message || 'Failed to update subject'); }
  };

  const removeSubject = async (subjectId) => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { error } = await supabase.from('subjects').delete().eq('id', subjectId).eq('team_id', team_id);
      if (error) throw error;
      onUpdateCourses(prev => prev.map(c => c.id === course.id ? { ...c, subjects: (c.subjects || []).filter(s => s.id !== subjectId) } : c));
      toast.success('Subject removed');
    } catch (err) { toast.error(err.message || 'Failed to delete subject'); }
  };

  if (!course) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 animate-slide-in-right">
      <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/50 flex flex-shrink-0 items-center justify-between">
        <h3 className="text-base font-semibold truncate">{course.id === 'new' ? 'Add Course' : (editMode ? 'Edit Course' : course.name)}</h3>
        <div className="flex flex-shrink-0 items-center gap-1">
          {editMode ? (
            <>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit}>
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              {course.id !== 'new' && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditMode(false)}>Cancel</Button>
              )}
            </>
          ) : (
            !readOnly && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={startEdit}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )
          )}
          <button className="w-7 h-7 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-400" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-5">
        {editMode ? (
          <div className="space-y-3 mb-6">
            <div><Label className="text-xs">Course Name</Label><Input className="mt-1" value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Algorithms" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Code</Label><Input className="mt-1" value={editForm.code || ''} onChange={e => setEditForm(f => ({ ...f, code: e.target.value }))} placeholder="e.g. CS-201" /></div>
              <div><Label className="text-xs">Department</Label><Input className="mt-1" value={editForm.department || ''} onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))} placeholder="e.g. Computer Science" /></div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <textarea 
                className="mt-1 w-full min-h-[80px] rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                value={editForm.description || ''} 
                onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description..."
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Priority</Label>
              <StarRating value={editForm.priority} onChange={v => setEditForm(f => ({ ...f, priority: v }))} />
            </div>
            {course.id === 'new' && (
              <div className="mt-4 p-3 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-900/50">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Initial Subjects ({newCourseSubjects.length})</Label>
                
                {newCourseSubjects.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {newCourseSubjects.map((sub, index) => (
                      <div key={index} className="flex items-center justify-between bg-white dark:bg-zinc-950 p-2 rounded-md border border-zinc-150 dark:border-zinc-800 text-xs">
                        <span className="font-medium">{sub.name} <span className="text-zinc-400">({sub.credit_hours} hrs)</span></span>
                        <button type="button" className="text-red-500 hover:text-red-700 font-bold" onClick={() => setNewCourseSubjects(prev => prev.filter((_, i) => i !== index))}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input 
                    className="flex-1 h-8 text-xs bg-white dark:bg-zinc-950" 
                    placeholder="Subject name" 
                    value={newSubForm.name} 
                    onChange={e => setNewSubForm(p => ({ ...p, name: e.target.value }))} 
                  />
                  <div className="relative w-20">
                    <Input 
                      className="w-full h-8 text-xs pl-2 pr-8 bg-white dark:bg-zinc-950" 
                      type="text" 
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Hrs" 
                      value={newSubForm.credit_hours} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '');
                        setNewSubForm(p => ({ ...p, credit_hours: parseInt(val) || 0 }));
                      }} 
                    />
                    <span className="absolute right-2 top-2 text-[0.55rem] text-zinc-400 font-bold uppercase">Hrs</span>
                  </div>
                  <Button 
                    type="button"
                    size="sm" 
                    variant="outline" 
                    className="h-8 text-xs shrink-0 px-2" 
                    onClick={() => {
                      if (!newSubForm.name.trim()) return;
                      setNewCourseSubjects(prev => [...prev, { name: newSubForm.name.trim(), credit_hours: newSubForm.credit_hours }]);
                      setNewSubForm({ name: '', credit_hours: 3 });
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            <div className="flex flex-wrap items-center gap-2 bg-zinc-50 dark:bg-zinc-800/50 p-3 rounded-lg border border-zinc-200 dark:border-zinc-700/50">
              <Badge variant="secondary" className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">{course.code}</Badge>
              {course.department && <Badge variant="outline" className="text-zinc-500">{course.department}</Badge>}
              <StarRating value={course.priority} readonly />
            </div>
            {course.description && (
              <div className="p-3 rounded-lg border border-zinc-100 dark:border-zinc-800/50 bg-zinc-50/50 dark:bg-zinc-900/50">
                <p className="text-xs text-zinc-500 leading-relaxed italic">"{course.description}"</p>
              </div>
            )}
          </div>
        )}

        {course.id !== 'new' && (
          <>
            <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Subjects ({(course.subjects || []).length})</h4>
            <div className="space-y-2">
              {(course.subjects || []).map((sub) => (
                <div key={sub.id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 group">
                  <div>
                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{sub.name}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-zinc-400">{sub.credit_hours || 0} total hrs</p>
                      <span className="text-[10px] text-zinc-300">•</span>
                      <p className="text-xs text-zinc-400">{sub.completed_hours || 0} completed</p>
                    </div>
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-1">
                      <button 
                        className="w-7 h-7 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-center text-zinc-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all" 
                        onClick={() => setEditingSubject(sub)}
                        title="Edit Subject"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        className="w-7 h-7 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center text-zinc-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all" 
                        onClick={() => removeSubject(sub.id)}
                        title="Delete Subject"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Edit Subject Dialog */}
            <Dialog open={!!editingSubject} onOpenChange={(open) => !open && setEditingSubject(null)}>
              <DialogContent className="sm:max-w-md" aria-describedby="edit-subject-description">
                <DialogHeader>
                  <DialogTitle>Edit Subject</DialogTitle>
                  <DialogDescription id="edit-subject-description">Modify subject details and credit hours.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="sub-name" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Subject Name</Label>
                    <Input id="sub-name" value={editingSubject?.name || ''} onChange={e => setEditingSubject(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Credit Hours</Label>
                      <div className="relative">
                        <Input 
                          type="text" 
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={editingSubject?.credit_hours || 0} 
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            setEditingSubject(p => ({ ...p, credit_hours: parseInt(val) || 0 }));
                          }} 
                          className="pr-12"
                        />
                        <span className="absolute right-3 top-2.5 text-[0.65rem] text-zinc-400 font-bold uppercase tracking-widest">Hrs</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Completed Hours</Label>
                      <div className="relative">
                        <Input 
                          type="text" 
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={editingSubject?.completed_hours || 0} 
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            setEditingSubject(p => ({ ...p, completed_hours: parseInt(val) || 0 }));
                          }} 
                          className="pr-12"
                        />
                        <span className="absolute right-3 top-2.5 text-[0.65rem] text-zinc-400 font-bold uppercase tracking-widest">Hrs</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-violet-50 dark:bg-violet-900/10 rounded-xl border border-violet-100 dark:border-violet-800/30">
                   <p className="text-[0.65rem] text-violet-600 dark:text-violet-400 leading-relaxed font-medium">
                     <b>Pro-tip:</b> The AI Studio calculates <i>Credit Hours - Completed Hours</i> to determine the remaining sessions needed for this subject.
                   </p>
                </div>
                </div>

                <div className="absolute bottom-4 right-4 z-50 pointer-events-none opacity-50 hover:opacity-100 transition-opacity">
                   <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm border border-zinc-200 dark:border-zinc-800 p-2 rounded-lg max-w-[200px] shadow-sm">
                      <p className="text-[8px] text-zinc-500 leading-tight">
                         <b>Privacy Notice:</b> All entity data is stored securely and used exclusively for internal academic scheduling and management. No data is shared with third-party advertising services.
                      </p>
                   </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={() => setEditingSubject(null)}>Cancel</Button>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => updateSubject(editingSubject)}>Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {!readOnly && (
              <>
                <div className="mt-4 p-3 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700">
                  <p className="text-xs font-medium text-zinc-500 mb-2 font-bold uppercase tracking-wider">Add Subject</p>
                  <div className="flex gap-2">
                    <Input className="flex-1 h-8 text-xs" placeholder="Subject name" value={subjectForm.name} onChange={e => setSubjectForm(p => ({ ...p, name: e.target.value }))} />
                    <div className="relative w-20">
                      <Input 
                        className="w-full h-8 text-xs pl-2 pr-8" 
                        type="text" 
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Hrs" 
                        value={subjectForm.credit_hours} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '');
                          setSubjectForm(p => ({ ...p, credit_hours: parseInt(val) || 0 }));
                        }} 
                      />
                      <span className="absolute right-2 top-2 text-[0.55rem] text-zinc-400 font-bold uppercase">Hrs</span>
                    </div>
                    <Button size="sm" variant="outline" className="h-8 text-xs shrink-0 px-2" onClick={addSubject}>Add</Button>
                  </div>
                  <p className="text-[0.55rem] text-zinc-500 mt-2 leading-relaxed italic">
                    <b>Note:</b> Credit Hours represent the total syllabus burden. The scheduler uses this to track progress and balance weekly workloads.
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="mt-6 text-red-600 hover:text-red-700 hover:bg-red-50 w-full justify-start" onClick={removeCourse}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Course
                </Button>
              </>
            )}
          </>
        )}
      </ScrollArea>
    </div>
  );
}
